/**
 * Context provider that loads and applies tenant i18n overrides.
 * Must be placed inside AuthProvider (needs tenant context)
 * and should wrap the main app content.
 */
import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth';
import { i18nOverridesApi } from '../../modules/admin/services/i18nOverridesApi';
import { applyTenantI18nOverrides } from '../../i18n/config';

/**
 * The APP-WIDE terms query — every `{{Product}}` / `{{companyGender}}` in the UI comes
 * from it. Exported because the Terminology admin page's save/reset mutations must
 * invalidate it: they own the `i18n-terminology` table query, and invalidating only
 * that one leaves the whole application rendering the previous wording (and the
 * previous gender) for `staleTime` — five minutes — with no way for the admin to tell
 * whether their change took.
 */
export const I18N_LOADER_QUERY_KEY = 'i18n-loader';

export function I18nOverridesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const { data } = useQuery({
    queryKey: [I18N_LOADER_QUERY_KEY, locale],
    queryFn: () => i18nOverridesApi.getLoaderData(locale),
    // Overrides are tenant-scoped and the route is `require_auth`, which 401s a
    // principal with no tenant. A `pending_setup` user (Setup Wizard) has none
    // yet, so asking would only add a doomed request to their console.
    enabled: !!user?.tenant_id,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (data) {
      applyTenantI18nOverrides(locale, data.terms, data.overrides);
    }
  }, [data, locale]);

  return <>{children}</>;
}
